import { Component, OnInit, Renderer2, OnDestroy, HostListener } from "@angular/core";
import { ActivatedRoute, NavigationEnd, Router, RouterEvent } from "@angular/router";
import { Contract } from "@edp-components/edp-contract";
import { environment } from "@edp-environments/environment";
import { Menu } from "@edp-models/menu.model";
import { EdpUser } from "@edp-models/user.model";
import { GetCampaigns, SetRecaptcha, SetRecaptchaResponse, ShowNewLocalToastr } from "@edp-store/actions/dashboard.actions";
import { GetCounter, SetLevelStatus } from "@edp-store/actions/user.actions";
import { GetContracts, GetPendingContracts, ShowContractSelection, SetUrlToRedirect } from "@edp-store/actions/contract.actions";
import { getContract, getContracts, getUrlToRedirect } from "@edp-store/selectors/contract.selectors";
import { getRecaptcha } from "@edp-store/selectors/dashboard.selectors";
import { getIframe } from "@edp-store/selectors/iframe.selectors";
import { getMainMenu, getShowHeaderAlert, getShowSidebar } from "@edp-store/selectors/layout.selectors";
import { getUser, getOnboarding as getOnboardingUser, getUserIsFirstLogin } from "@edp-store/selectors/user.selectors";
import { IAppState } from "@edp-store/state/app.state";
import { select, Store } from "@ngrx/store";
import { combineLatest, Subject } from "rxjs";
import { filter, take, takeUntil } from "rxjs/operators";
import { ICustomUrl } from "@edp-models/deep-link.model";
import { GoogleAnalyticsService } from "@edp-services/google-analytics.service";
import { GetOperations } from "@edp-store/actions/operations.actions";
import { RefreshMainMenu, SetNeedLoadUserPoints, ShowLoader } from "@edp-store/actions/layout.actions";
import { AppBannerService } from "@edp-services/app-banner.service";
import { UnavailabilityService } from "@edp-services/unavailability.service";
import { IUnavailability } from "@edp-models/unavailability.model";
import { RegionActionsService } from "@edp-services/region-actions.service";
import { getContractEs, getContractsEs } from "@edp-store/selectors/ES/contract-es.selectors";
import { IAppEsState } from "@edp-store/state/ES/app-es.state";
import { ContractEs } from "@edp-models/ES/es-contract.model";
import { SelectContractEs, ShowContractSelectionEs } from "@edp-store/actions/ES/contract-es.actions";
import { RegionService } from "@edp-services/region.service";
import { RegionEnum } from "@edp-enums/region.enum";
import { getOnboarding, getOnboardingWhatsNew } from "@edp-store/selectors/onboaring.selectors";
import { OnboardingService } from "@edp-services/onboarding.service";
import { GuidedTourService } from "@edp-components/ngx-guided-tour/guided-tour.service";
import {
    SetCompleteOnboarding,
    SetOnboarding,
    SetOnboardingWhatsNew,
    WhatsNewRequestsAPIError,
} from "@edp-store/actions/onboarding.actions";
import { Onboarding, OnboardingWhatsNew } from "@edp-models/onboarding.model";
import { OnboardingTypeEnum } from "@edp-enums/onboarding-type.enum";
import { EdpTermsConditionsModalComponent } from "@edp-components/ES/edp-terms-conditions-modal/edp-terms-conditions-modal.component";
import { EdpModalService } from "@edp-components/edp-modal";
import { NgbModalOptions } from "@ng-bootstrap/ng-bootstrap";
import { BenefitsService } from "@edp-services/benefits.service";
import { getListBenefitsUsersDetails } from "@edp-store/selectors/benefits.selectors";
import { GetTransactions } from "@edp-store/actions/benefits.actions";
import { EdpDefineHouseTermsConditionsService } from "@edp-services/edp-define-house-terms-conditions.service";

@Component({
    selector: "layout",
    templateUrl: "./layout.component.html",
    styleUrls: ["./layout.component.scss"],
})
export class LayoutComponent implements OnInit, OnDestroy {
    public isApp = false;
    public hideMenuAndHeader: boolean;
    public onboarding: boolean;
    public showCaptcha: boolean;
    public showLoader: boolean;
    public showIframe: boolean;
    public showSidebar: boolean;
    public hideSubMenu: boolean;
    public hideTopMenuContainer: boolean;
    public levelModalViewed: boolean;
    public showAlert: boolean;
    public removeHeaderPadding: boolean;
    private unsubscribe = new Subject();
    private unsubscribeOnboarding = new Subject();
    private unsubscribeOnboardingWhatsNew = new Subject();
    public showAppBanner = false;
    public onboardingTour: Onboarding | null;
    public onboardingTourIsShowing = false;
    public onboardingTourWhatsNew: OnboardingWhatsNew | null;
    private routerNavigation: NavigationEnd;
    public unavailability: IUnavailability | undefined;
    public showTermsAndConditions = false;

    @HostListener("window:beforeunload") onBeforeUnload() {
        if (!!this.onboardingTourIsShowing && !!this.onboardingTour) {
            this.store.dispatch(new SetCompleteOnboarding(this.onboardingTour.id));
        }
    }

    constructor(
        private store: Store<IAppState>,
        private storeEs: Store<IAppEsState>,
        private renderer: Renderer2,
        private activatedRoute: ActivatedRoute,
        private router: Router,
        public googleAnalyticsService: GoogleAnalyticsService,
        private readonly appBannerService: AppBannerService,
        private unavailabilityService: UnavailabilityService,
        private regionActionsService: RegionActionsService,
        private regionService: RegionService,
        private onboardingService: OnboardingService,
        private guidedTourService: GuidedTourService,
        private edpModalService: EdpModalService,
        private benefitsService: BenefitsService,
        private defineHouseTermsConditionsService: EdpDefineHouseTermsConditionsService
    ) {
        this.defineHouseTermsConditionsService.showTermsConditions$.subscribe((show: boolean) => {
            this.showTermsAndConditions = show;
        });

        this.showAppBanner = this.appBannerService.show();
        this.router.events.subscribe((event: RouterEvent) => {
            if (event instanceof NavigationEnd) {
                this.getParamToHideMenu();

                this.routerNavigation = event;

                const route: ActivatedRoute = this.getLastChild(this.activatedRoute);
                this.checkContract(route);

                this.store.pipe(select(getUrlToRedirect), take(1)).subscribe((linkConfigured: ICustomUrl) => {
                    if (linkConfigured && linkConfigured.redirectInProgress && this.routerNavigation.url.includes(linkConfigured.url)) {
                        this.store.dispatch(new SetUrlToRedirect(null));
                    }
                });

                this.googleAnalyticsService.pageChangeEvent();

                this.store.dispatch(new RefreshMainMenu());

                window.scrollTo(0, 0);
            }
        });

        this.store.pipe(select(getRecaptcha)).subscribe((captcha: boolean) => {
            this.showCaptcha = captcha;
            if (this.showCaptcha) {
                setTimeout(() => {
                    grecaptcha.execute();
                }, 1000);
            }
        });

        if (this.regionService.getRegion() !== RegionEnum.ES) {
            this.store.pipe(select(getIframe)).subscribe(data => {
                this.showIframe = data !== null && !data.preload;
            });
        }

        this.store.pipe(select(getOnboardingUser)).subscribe(data => {
            this.onboarding = data;
        });
        this.onboarding = localStorage.getItem("user-onboarding") === "false" ? false : true;

        this.store.pipe(select(getShowSidebar)).subscribe(data => {
            this.showSidebar = data.showSidebar;

            if (this.showSidebar) {
                this.renderer.addClass(document.body, "menu-open");
            } else {
                this.renderer.removeClass(document.body, "menu-open");
                if (
                    this.router.routerState.snapshot.url.includes("energia") &&
                    this.router.routerState.snapshot.url.includes("inicio") &&
                    data.needLoadUserPoints
                ) {
                    this.checkLoad();
                }
            }
        });

        this.store.pipe(select(getMainMenu)).subscribe((menuState: Menu[]) => {
            menuState.forEach(menuItem => {
                if (
                    (menuItem.isActive && menuItem.routerPath && menuItem.submenu) ||
                    (menuItem.isActive && menuItem.name === "MENU.MVP.LEISURE")
                ) {
                    this.hideSubMenu = !menuItem.submenu.length;
                }
                this.hideTopMenuContainer = menuItem.isActive && ["perfil"].includes(menuItem.grouping);
            });
        });

        this.store.pipe(takeUntil(this.unsubscribe), select(getShowHeaderAlert)).subscribe((alertStatus: boolean) => {
            this.showAlert = alertStatus;
        });
    }

    ngOnInit() {
        this.isApp = environment.isApp;
        this.regionActionsService.getContracts();
        this.loadCampaigns();
        this.loadUnavailability();
        this.getOnboarding();
        this.getOnboardingWhatsNew();
        this.handleUserBenefit();
    }

    private showModalTermsAndConditionES(): void {
        const options: NgbModalOptions = { backdrop: "static", size: "sm" };
        this.edpModalService.custom(EdpTermsConditionsModalComponent, options, {}).then(() => {});
    }

    private checkContractES(route: ActivatedRoute): void {
        combineLatest([
            this.storeEs.pipe(select(getContractEs), takeUntil(this.unsubscribe)),
            this.storeEs.pipe(select(getContractsEs), takeUntil(this.unsubscribe)),
            this.store.pipe(select(getUserIsFirstLogin)),
        ]).subscribe(([contract, contracts, isFirstLogin]: [ContractEs, ContractEs[], boolean]) => {
            if (isFirstLogin) {
                return this.showModalTermsAndConditionES();
            }


            if (contracts.length === 1) {
                this.store.dispatch(new SelectContractEs(contracts[0]));
            }

            if (contracts.length > 1 && !contract && !route.snapshot.data.skipContractSelection) {
                this.store.dispatch(new ShowContractSelectionEs());
                this.store.dispatch(new ShowLoader(false));
            }
        });
    }

    private checkContract(route: ActivatedRoute): void {
        if (this.regionService.getRegion() === RegionEnum.ES) {
            this.checkContractES(route);
            return;
        }

        combineLatest([this.store.pipe(select(getContracts), take(1)), this.store.pipe(select(getContract), take(1))]).subscribe(
            ([contracts, contract]: [Contract[], Contract]) => {
                if (contracts.length && !contract && !route.snapshot.data.skipContractSelection) {
                    this.store.dispatch(new ShowContractSelection());
                    this.store.dispatch(new ShowLoader(false));
                }
            }
        );
    }

    private checkLoad() {
        const delayTransitionSideBar = 750;
        setTimeout(() => {
            if (document.body.getElementsByClassName("show-side-bar").length === 0) {
                this.store.dispatch(new SetNeedLoadUserPoints(false));
                this.store.dispatch(new GetCounter());
            }
        }, delayTransitionSideBar);
    }

    private loadUnavailability() {
        this.unavailabilityService.unavailability$.subscribe(data => {
            this.unavailability = data;
        });
    }

    public displayLoader(event): void {
        this.showLoader = event;
    }

    public resolved(recaptchaResponse?: string): void {
        this.store.dispatch(new SetRecaptchaResponse(recaptchaResponse));
        grecaptcha.reset();
        this.store.dispatch(new SetRecaptcha(false));
    }

    public getParamToHideMenu() {
        const route: ActivatedRoute = this.getLastChild(this.activatedRoute);
        this.hideMenuAndHeader = false;
        route.data.subscribe(params => {
            this.hideMenuAndHeader = params.hideMenuAndHeaderContract ? true : false;
            this.removeHeaderPadding = params.noHeaderPadding || false;
        });
    }

    private getLastChild(child: ActivatedRoute) {
        return child.firstChild ? this.getLastChild(child.firstChild) : child;
    }

    private loadCampaigns() {
        if (this.regionService.getRegion() === RegionEnum.ES) {
            combineLatest([
                this.store.pipe(select(getUser), takeUntil(this.unsubscribe)),
                this.storeEs.pipe(select(getContractEs), takeUntil(this.unsubscribe)),
            ]).subscribe(([user, contract]: [EdpUser, ContractEs]) => {
                if (user && contract) {
                    this.store.dispatch(new GetCampaigns(user.partnerNumber, contract.accountId));
                }
            });
            return;
        }

        combineLatest([
            this.store.pipe(select(getUser), takeUntil(this.unsubscribe)),
            this.store.pipe(select(getContract), takeUntil(this.unsubscribe)),
        ]).subscribe(([user, contract]: [EdpUser, Contract]) => {
            if (user && contract) {
                this.store.dispatch(new GetCampaigns(user.partnerNumber, contract.AccountNumber));
            }
        });
    }

    private checkURL(page: string): boolean {
        const currentURL = this.router.routerState.snapshot.url.split("/");
        const onboardingPage = page.split("/");
        const indexParm = onboardingPage.findIndex(i => i.includes(":"));

        if (indexParm >= 0) {
            if (
                currentURL.some(i => i.includes("energia")) &&
                currentURL.some(i => i.includes("inicio")) &&
                onboardingPage.some(i => i.includes("energia")) &&
                onboardingPage.some(i => i.includes("inicio"))
            ) {
                return true;
            }
            onboardingPage[indexParm] = currentURL[indexParm];
        }
        return currentURL.toString().includes(onboardingPage.toString());
    }

    private checkDeeplinkWhatsNew(page: string): boolean {
        const currentURL = this.router.routerState.snapshot.url;
        return currentURL.includes(page);
    }

    private pageOnboarding(): void {
        if (this.onboardingTour.type !== OnboardingTypeEnum.Intro && !this.checkURL(this.onboardingTour.page)) {
            return;
        }
        this.onboardingTourIsShowing = true;
        this.unsubscribeOnboarding.next();
        this.unsubscribeOnboarding.complete();
        const tour = this.onboardingService.getOnboarding(this.onboardingTour.id, this.onboardingTour.step, this.onboardingTour.type);
        this.store.dispatch(new SetOnboarding(null));

        setTimeout(() => {
            this.guidedTourService.startTour(tour);
        }, 1000);
    }

    private getOnboarding(): void {
        combineLatest([
            this.store.pipe(select(getOnboarding)),
            this.store.pipe(
                select(getContract),
                filter(c => !!c)
            ),
        ])
            .pipe(take(1))
            .subscribe(([onboarding]) => {
                this.onboardingTour = onboarding;
                if (!!this.onboardingTour) {
                    this.pageOnboarding();
                } else {
                    this.onboardingTourIsShowing = false;
                }
            });

        combineLatest([
            this.store.pipe(select(getOnboarding)),
            this.router.events.pipe(filter(event => event instanceof NavigationEnd)),
            this.store.pipe(
                select(getContract),
                filter(c => !!c)
            ),
        ])
            .pipe(takeUntil(this.unsubscribeOnboarding))
            .subscribe(([onboarding]) => {
                this.onboardingTour = onboarding;
                if (!!this.onboardingTour) {
                    this.pageOnboarding();
                }
            });
    }

    private pageOnboardingWhatsNew(): void {
        // check if orboarding is deeplink to check url
        if (Array.isArray(this.onboardingTourWhatsNew.action) || !this.onboardingTourWhatsNew.action.url) {
            this.unsubscribeOnboardingWhatsNew.next();
            this.unsubscribeOnboardingWhatsNew.complete();
            return;
        }

        if (!this.checkDeeplinkWhatsNew(this.onboardingTourWhatsNew.action.url)) {
            return;
        }

        this.unsubscribeOnboardingWhatsNew.next();
        this.unsubscribeOnboardingWhatsNew.complete();
        this.store.dispatch(new SetCompleteOnboarding(this.onboardingTourWhatsNew.id));
        this.store.dispatch(new SetOnboardingWhatsNew(null));
        // Dispatch error to disappear in dashboard
        this.store.dispatch(new WhatsNewRequestsAPIError(new Error("completed")));
    }

    private handleUserBenefit(): void {
        combineLatest([
            this.store.select(getContract).pipe(filter(c => !!c)),
            this.store.select(getListBenefitsUsersDetails).pipe(filter(b => !!b)),
        ])
            .pipe(take(1))
            .subscribe(([contract, listUsersBenefits]) => {
                if (listUsersBenefits.length > 0) {
                    const userBenefit = this.benefitsService.setUserBenefitByNIF(contract, listUsersBenefits);
                    if (!!userBenefit) {
                        this.store.dispatch(new GetTransactions({ pn: userBenefit.pn }));
                    }
                }
            });
    }

    private getOnboardingWhatsNew(): void {
        combineLatest([
            this.store.pipe(select(getOnboardingWhatsNew)),
            this.store.pipe(
                select(getContract),
                filter(c => !!c)
            ),
        ])
            .pipe(take(1))
            .subscribe(([onboarding]) => {
                this.onboardingTourWhatsNew = onboarding;
                if (!!this.onboardingTourWhatsNew) {
                    this.pageOnboardingWhatsNew();
                }
            });

        combineLatest([
            this.store.pipe(select(getOnboardingWhatsNew)),
            this.router.events.pipe(filter(event => event instanceof NavigationEnd)),
            this.store.pipe(
                select(getContract),
                filter(c => !!c)
            ),
        ])
            .pipe(takeUntil(this.unsubscribeOnboardingWhatsNew))
            .subscribe(([onboarding]) => {
                this.onboardingTourWhatsNew = onboarding;
                if (!!this.onboardingTourWhatsNew) {
                    this.pageOnboardingWhatsNew();
                }
            });
    }

    public ngOnDestroy() {
        this.unsubscribe.next();
        this.unsubscribe.complete();
    }
}
